import { useEffect, useRef, useState } from 'react';

/**
 * Renders the detail action menu experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.label - label value.
 * @param {*} props.primaryAction - primary action value.
 * @param {*} props.actions - actions collection.
 * @returns {JSX.Element} The rendered interface.
 */
export default function DetailActionMenu({ label = 'Entry actions', primaryAction = null, actions = [] }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const visibleActions = actions.filter(Boolean);

  useEffect(() => {
    setIsOpen(false);
  }, [actions.length, primaryAction?.label]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const focusFrame = window.requestAnimationFrame(() => {
      menuRef.current?.querySelector('[role="menuitem"]:not([disabled])')?.focus();
    });
    /**
     * Performs on outside click.
     *
     * @param {*} event - Browser or React event that triggered the action.
     * @returns {void} No value; the operation applies its documented side effects.
     */
    const closeOnOutsideClick = (event) => {
      if (!containerRef.current?.contains(event.target)) setIsOpen(false);
    };
    /**
     * Performs on escape.
     *
     * @param {*} event - Browser or React event that triggered the action.
     * @returns {void} No value; the operation applies its documented side effects.
     */
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isOpen]);

  if (!primaryAction && !visibleActions.length) return null;

  return (
    <div className="detail-header-actions" ref={containerRef}>
      {primaryAction && (
        <button type="button" className="detail-edit-action" disabled={primaryAction.disabled} onClick={primaryAction.onSelect}>
          {primaryAction.label}
        </button>
      )}
      {visibleActions.length > 0 && (
        <div className="recipe-detail-menu-wrap">
          <button
            ref={triggerRef}
            type="button"
            className="recipe-menu-button"
            aria-label={label}
            aria-expanded={isOpen}
            aria-haspopup="menu"
            onClick={() => setIsOpen((current) => !current)}
          >
            <span aria-hidden="true">⋯</span>
          </button>
          {isOpen && (
            <div
              ref={menuRef}
              className="recipe-detail-menu"
              role="menu"
              onKeyDown={(event) => {
                if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
                event.preventDefault();
                const items = [...event.currentTarget.querySelectorAll('[role="menuitem"]:not([disabled])')];
                if (!items.length) return;
                const currentIndex = items.indexOf(document.activeElement);
                const nextIndex = event.key === 'Home'
                  ? 0
                  : event.key === 'End'
                    ? items.length - 1
                    : event.key === 'ArrowDown'
                      ? (currentIndex + 1 + items.length) % items.length
                      : (currentIndex - 1 + items.length) % items.length;
                items[nextIndex].focus();
              }}
            >
              {visibleActions.map((action, index) => (
                <button
                  key={action.label}
                  type="button"
                  role="menuitem"
                  className={`${action.variant === 'danger' ? 'danger' : ''} ${action.variant === 'danger' && index > 0 ? 'separated' : ''}`.trim()}
                  disabled={action.disabled}
                  onClick={() => {
                    setIsOpen(false);
                    action.onSelect?.();
                  }}
                >
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
